import { useState } from 'react';
import { Clock, Crown, ArrowUpRight } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useCopy, fill } from '../../i18n';
import { plural } from '../../i18n/format';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import PricingPage from './PricingPage';

interface TrialBannerProps {
  collapsed?: boolean;
}

export default function TrialBanner({ collapsed = false }: TrialBannerProps) {
  const { isTrialing, isTrialExpired, trialDaysRemaining, isFree, subscription } = useSubscription();
  const t = useCopy().account.billing.trialBanner;
  const [showPricing, setShowPricing] = useState(false);

  // Mostrar banner se: trial ativo, OU trial expirado (caiu para Free)
  const showExpired = isFree && subscription?.status === 'trialing' && trialDaysRemaining === 0;
  const showActive = isTrialing && trialDaysRemaining !== null && trialDaysRemaining > 0;

  if (!showActive && !showExpired) return null;

  // Determinar estilo baseado nos dias restantes
  const getStyle = () => {
    if (showExpired) return { bg: 'bg-amber-500/15', text: 'text-amber-300', border: 'border-amber-500/30' };
    if (trialDaysRemaining! <= 1) return { bg: 'bg-red-500/15', text: 'text-red-300', border: 'border-red-500/30' };
    if (trialDaysRemaining! <= 3) return { bg: 'bg-amber-500/15', text: 'text-amber-300', border: 'border-amber-500/30' };
    return { bg: 'bg-indigo-500/15', text: 'text-indigo-300', border: 'border-indigo-500/30' };
  };

  const style = getStyle();

  const getMessage = () => {
    if (showExpired) return t.expiredShort;
    if (trialDaysRemaining === 1) return t.expiresTodayShort;
    return fill(plural(trialDaysRemaining ?? 0, t.daysShort), { count: trialDaysRemaining ?? 0 });
  };

  const getCtaText = () => {
    if (showExpired) return t.ctaUpgrade;
    if (trialDaysRemaining! <= 3) return t.ctaSubscribeNow;
    return t.ctaSubscribe;
  };

  // Sidebar colapsada: ícone com tooltip
  if (collapsed) {
    return (
      <>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              data-tour="trial-banner"
              className={cn(
                'flex items-center justify-center w-full p-2.5 transition-all duration-150',
                style.bg, 'hover:opacity-80'
              )}
              onClick={() => setShowPricing(true)}
            >
              {showExpired ? (
                <ArrowUpRight size={20} className={style.text} />
              ) : (
                <Clock size={20} className={style.text} />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>{getMessage()} — {getCtaText()}</p>
          </TooltipContent>
        </Tooltip>
        {showPricing && <PricingPage onClose={() => setShowPricing(false)} />}
      </>
    );
  }

  // Sidebar expandida: banner completo
  return (
    <>
      <div
        data-tour="trial-banner"
        className={cn(
          'mx-2 my-1.5 p-3 rounded-lg border transition-all duration-200',
          style.bg, style.border
        )}
      >
        <div className="flex items-center gap-2 mb-2">
          <Crown size={14} className={style.text} />
          <span className={cn('text-xs font-semibold', style.text)}>
            {showExpired ? t.titleExpired : t.titleActive}
          </span>
        </div>

        {showActive && (
          <div className="flex items-center gap-1.5 mb-2.5">
            <Clock size={12} className="text-white/50" />
            <span className="text-[11px] text-white/60">
              {trialDaysRemaining === 1
                ? t.expiresToday
                : fill(plural(trialDaysRemaining ?? 0, t.daysRemaining), { count: trialDaysRemaining ?? 0 })}
            </span>
          </div>
        )}

        {showExpired && (
          <p className="text-[11px] text-white/50 mb-2.5 leading-relaxed">
            {t.expiredBody}
          </p>
        )}

        <button
          className={cn(
            'w-full py-1.5 px-3 rounded-md text-xs font-medium transition-all duration-150',
            'flex items-center justify-center gap-1.5',
            showExpired || trialDaysRemaining! <= 3
              ? 'bg-amber-500 hover:bg-amber-600 text-white'
              : 'bg-indigo-500 hover:bg-indigo-600 text-white'
          )}
          onClick={() => setShowPricing(true)}
        >
          <ArrowUpRight size={13} />
          {getCtaText()}
        </button>
      </div>

      {showPricing && <PricingPage onClose={() => setShowPricing(false)} />}
    </>
  );
}
